/**
 * Sharing — the portable half.
 *
 * Deliberately free of React, Next and anything Finseats-specific, because the
 * same three problems show up on every product in the portfolio and only the
 * surface names differ:
 *
 *   1. A shared link has to be attributable, or "does sharing work?" stays an
 *      opinion. Every link carries UTM parameters, which is the one scheme
 *      PostHog, GA and Search Console all already understand — no bespoke
 *      parameter to teach three tools about.
 *   2. The share ACT and the share ARRIVAL are different events with different
 *      denominators. Counting only clicks measures enthusiasm; counting only
 *      arrivals measures reach. The pair is what tells you whether the people
 *      being sent are worth anything.
 *   3. Channel intent URLs are fiddly and every product gets them subtly wrong
 *      once (X moved to x.com/intent/post, LinkedIn dropped title/summary).
 *
 * Copy this file into another product and only SHARE_SURFACES changes.
 */

/** Where the share happened. Per-product; everything else here is portable. */
export const SHARE_SURFACES = [
  "job",
  "search",
  "organization",
  "location",
  "category",
  "guide",
  "portfolio",
  "application",
] as const;
export type ShareSurface = (typeof SHARE_SURFACES)[number];

/**
 * How it was shared. `native` is the OS share sheet — it does not tell us which
 * app the person picked, and pretending otherwise would invent data, so it
 * stays its own channel rather than being guessed at.
 */
export const SHARE_CHANNELS = ["copy", "native", "linkedin", "x", "whatsapp", "email"] as const;
export type ShareChannel = (typeof SHARE_CHANNELS)[number];

/** utm_source value that marks a visit as arriving from one of our share links. */
export const SHARE_SOURCE = "share";

/**
 * Tag a URL for attribution. Existing query parameters are preserved — sharing
 * a filtered search is the main reason anyone shares this product, and dropping
 * the filters would share a different page than the one being looked at.
 */
export function shareUrl(
  absoluteUrl: string,
  surface: ShareSurface,
  channel: ShareChannel,
): string {
  const u = new URL(absoluteUrl);
  u.searchParams.set("utm_source", SHARE_SOURCE);
  u.searchParams.set("utm_medium", channel);
  u.searchParams.set("utm_campaign", surface);
  return u.toString();
}

/**
 * Did this visit arrive from a shared link, and from where?
 *
 * Returns null for everything else, so the caller fires nothing rather than an
 * event with empty properties — an "unknown" bucket that swallows all organic
 * traffic is worse than no row at all.
 */
export function readShareArrival(
  search: string,
): { surface: string; channel: string } | null {
  const p = new URLSearchParams(search);
  if (p.get("utm_source") !== SHARE_SOURCE) return null;
  return {
    surface: p.get("utm_campaign") || "unknown",
    channel: p.get("utm_medium") || "unknown",
  };
}

/**
 * The intent URL for a channel, or null when the channel isn't a link (copy and
 * the native sheet are handled by the caller).
 *
 * LinkedIn ignores `title`/`summary` on /sharing/share-offsite and reads the
 * target page's OpenGraph tags instead, so only the url is sent — passing the
 * rest would be cargo cult.
 */
export function channelHref(
  channel: ShareChannel,
  url: string,
  title: string,
): string | null {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  switch (channel) {
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    case "x":
      // x.com/intent/post — twitter.com/intent/tweet still redirects, but the
      // redirect drops the referrer on some clients, which loses attribution.
      return `https://x.com/intent/post?url=${u}&text=${t}`;
    case "whatsapp":
      return `https://wa.me/?text=${t}%20${u}`;
    case "email":
      return `mailto:?subject=${t}&body=${u}`;
    case "copy":
    case "native":
      return null;
  }
}

export const CHANNEL_LABEL: Record<ShareChannel, string> = {
  copy: "Copy link",
  native: "Share",
  linkedin: "LinkedIn",
  x: "X",
  whatsapp: "WhatsApp",
  email: "Email",
};
